import React from 'react';

export type PendingDaysRange = { start: string; end: string };

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** An ISO calendar date as a local-noon Date, or null when it is not one.
 *  Built from local components and anchored at noon so neither the parse nor
 *  a DST shift can move it to a neighbouring day (no `toISOString`, no UTC
 *  parse of a bare date string). */
const localNoon = (iso: string): Date | null => {
  const m = ISO_DATE.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(year, month, day, 12, 0, 0, 0);
  if (d.getFullYear() !== year || d.getMonth() !== month || d.getDate() !== day) return null;
  return d;
};

/** The longest range drawn. A longer one renders nothing at all rather than a
 *  truncated shape, which would misstate the plan's length (and an unbounded
 *  range would build one row per day). */
export const MAX_PENDING_DAYS = 60;

/** One Date per calendar day of the range, inclusive; empty when the range is
 *  invalid, ends before it starts, or is longer than MAX_PENDING_DAYS. */
export const expandDays = (dates: PendingDaysRange | null): Date[] => {
  if (!dates) return [];
  const start = localNoon(dates.start);
  const end = localNoon(dates.end);
  if (!start || !end || end < start) return [];
  const days: Date[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 12);
  while (cursor <= end) {
    if (days.length === MAX_PENDING_DAYS) return [];
    days.push(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate(), 12));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

/**
 * The plan's shape while it builds (fidelity decision 8, P building()): one
 * numbered, weekday-labelled empty slot per day of the range, two hair-rule
 * bars in each. Renders nothing without a known range — no production caller
 * supplies dates today; the prop is the seam for one.
 */
const PendingDays: React.FC<{ dates: PendingDaysRange | null }> = ({ dates }) => {
  const days = expandDays(dates);
  if (days.length === 0) return null;
  return (
    <div className="pending-days">
      {days.map((day, i) => {
        const weekday = day.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase();
        return (
          <div key={i} className="pend" data-testid="pending-day">
            <div className="dnum">{`${i + 1} ${weekday}`}</div>
            <div className="pend-bars" aria-hidden="true">
              <i style={{ width: '62%' }} />
              <i style={{ width: '40%' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PendingDays;
